import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getWorkflow, getWorkflowList } from '@/services/workflow.service';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { SortOrderOptions, SortOrderValues } from '@/types/sort';
import { QueryKey } from '@/lib/queryKeys';

export const useWorkflow = (id: string, options?: object) => {
  const workflowQueryFn = useAuthenticatedQueryFn(getWorkflow);

  const {
    data: workflow = [],
    isPending: workflowIsPending,
    isFetching: workflowIsFetching,
    error: workflowError,
    refetch: refetchWorkflow,
  } = useQuery({
    queryKey: [QueryKey.Workflow, id],
    queryFn: () => workflowQueryFn(id),
    ...options,
  });

  return {
    workflow,
    workflowIsFetching,
    workflowIsPending,
    workflowError,
    refetchWorkflow,
  };
};

export const useWorkflowList = (
  params: {
    pagination: { limit: number | null; offset: number | null };
    sort: { order: string; value: string };
    queryString: string;
  },
  options?: object,
) => {
  const workflowListQueryFn = useAuthenticatedQueryFn(getWorkflowList);

  const {
    data: workflowList = {
      workflows: [],
      pagination: { count: 0, limit: 0, offset: 0 },
      sort: { order: SortOrderOptions.ASC, value: SortOrderValues.CREATED_AT },
    },
    isPending,
    isFetching,
    error,
    isFetched,
    refetch,
  } = useQuery({
    queryKey: [QueryKey.WorkflowList, params.pagination, params.queryString, params.sort],
    queryFn: () => workflowListQueryFn(params),
    placeholderData: keepPreviousData,
    ...options,
  });

  return {
    data: workflowList.workflows,
    pagination: workflowList.pagination,
    isFetching,
    isPending,
    isFetched,
    error,
    refetch,
  };
};
